// Creative management types for campaigns, creatives, and assets

export interface Campaign {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  creativeIds: string[];
}

export interface Creative {
  id: string;
  campaignId: string;
  name: string;
  primaryFormatId: string;
  templateId: string;
  content: CreativeContent;
  // Full template state (JSON stringified to avoid IndexedDB issues with complex objects)
  templateData?: string;
  // Timeline animations (JSON stringified)
  timelineData?: string;
  // Variable values
  variableValues?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl?: string;
}

export interface CreativeContent {
  headline: string;
  headlineIdx: number | 'custom';
  customHeadline?: string;
  subhead?: string;
  cta: string;
  showSubhead: boolean;
  showLogo: boolean;
  showBadges: boolean;
}

export interface Asset {
  id: string;
  campaignId?: string;
  name: string;
  type: 'image' | 'font' | 'design';
  mimeType: string;
  size: number;
  blob?: Blob;
  url?: string; // For external assets
  createdAt: string;
  metadata?: Record<string, unknown>;
}

// Editor state
export interface EditorState {
  currentCreativeId: string | null;
  currentCampaignId: string | null;
  selectedFormatId: string;
  content: CreativeContent;
  zoom: number;
  showGrid: boolean;
  selectedLayerIds: string[];
  // Animation state
  isPlaying: boolean;
  currentTime: number; // 0-1 normalized
  duration: number; // ms
}

// Export state
export interface ExportTask {
  id: string;
  creativeId: string;
  format: import('./design').ExportFormat;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  result?: Blob;
  error?: string;
  createdAt: string;
}

export interface ExportState {
  tasks: ExportTask[];
  currentTaskId: string | null;
}

// Default content
export const DEFAULT_HEADLINES: { headline: string; subhead: string }[] = [
  {
    headline: 'They steal your data to charge you more.',
    subhead: 'Stop paying surveillance taxes. mooncloak hides what you do from every middleman in the chain.',
  },
  {
    headline: "Privacy isn't hiding. It's being yourself.",
    subhead: 'Decentralized VPN. Zero logs. No account. The neutral baseline, restored.',
  },
  {
    headline: 'Go dark. Stay bright.',
    subhead: 'Privacy-first VPN powered by WireGuard, Nym mixnet, and Mysterium. No central servers. No one watching.',
  },
  {
    headline: "Your data is the product. Until it isn't.",
    subhead: "mooncloak re-routes your traffic through decentralized protocols. We couldn't sell your data if we wanted to.",
  },
  {
    headline: 'You have nothing to hide. Hide it anyway.',
    subhead: 'Privacy is the baseline. Treat yours like the right it is.',
  },
];

export const DEFAULT_CREATIVE_CONTENT: CreativeContent = {
  headline: DEFAULT_HEADLINES[0].headline,
  headlineIdx: 0,
  subhead: DEFAULT_HEADLINES[0].subhead,
  cta: 'Get mooncloak',
  showSubhead: true,
  showLogo: true,
  showBadges: false,
};

// Legacy aliases for migration compatibility
/** @deprecated Use Creative instead */
export type Project = Creative;
/** @deprecated Use CreativeContent instead */
export type ProjectContent = CreativeContent;
/** @deprecated Use DEFAULT_CREATIVE_CONTENT instead */
export const DEFAULT_PROJECT_CONTENT = DEFAULT_CREATIVE_CONTENT;
